import { AppFunctionComponent } from "../../../../types"
import React, { ComponentProps } from "react"
import styled, { css } from "styled-components"
import RichText from "../../../../resolvers/rich-text/rich-text.resolver"
import { ListBuilder, ListLayout, ListSize } from "../../../../types"
import AccordionList from "../../../../ui/components/accordion/accordion-list.component"
import { annotationStyles } from "../../general-styles.component"
import {
  BackgroundWrapper,
  default as Section,
  default as StandardSection,
  FullSection,
  MediumSection,
  reduceEmptySpaces,
  SectionHeading,
  SectionLayout,
  SmallSection,
} from "../section.component"
import { ScrollAnchor } from "../custom/innovation-lab-form/innovation-lab.component"
import SectionBackgroundWrapper from "../../../../helpers/sitebuilder-background-wrapper/section-background-wrapper"

const spacingStyles = css`
  margin-top: 50px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    margin-top: 100px;
  }
`

const textWithDescriptionStyle = css`
  p:first-child {
    ${annotationStyles}
    margin-top: 0;
    opacity: 0.66;
    line-height: ${18 / 10}em;
    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      margin-top: 15px;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    div {
      width: 100%;
      text-align: left;
    }
  }
`

export const BasicList = styled.ul`
  ${spacingStyles};

  list-style-type: none;
  padding: 0;

  display: grid;
  grid-auto-rows: auto;
  grid-row-gap: 50px;
  grid-column-gap: 40px;
`

const OneColumnWithLeftTextList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr;
  }
`

const OneColumnList = styled(BasicList)`
  grid-row-gap: 30px;
`

export const TwoColumnsList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 120px;
  }
`

const TwoColumnsLastWideList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 120px;
    li:last-of-type {
      grid-column: span 2;
    }
  }
`

export const ThreeColumnsLastTwoSplitList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 2fr;
  }
`

export const ThreeColumnsList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

export const FourColumnsList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

export const FiveColumnsList = styled(BasicList)`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
`

const SixColumnsList = styled(BasicList)`
  grid-column-gap: 15px;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    grid-template-columns: 1fr 1fr 1fr;

    img {
      max-width: 100px;
      max-height: 100px;
    }
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;

    img {
      max-width: 80px;
      max-height: 80px;
    }
  }
`

const EcommerceSingleColumnList = styled(BasicList)``

const OneColumnNoMarginList = styled(BasicList)`
  margin-top: 0;
`

const BasicAccordionList = styled(AccordionList)`
  ${spacingStyles};
`

const CareerList = styled(BasicList)`
  margin-top: 20px;
  grid-row-gap: 0px;

  &:first-of-type {
    border-top: 1px solid ${(props) => props.theme.color.borderLight};
  }
`

const ListWrapper: AppFunctionComponent<
  ComponentProps<typeof Section> & {
    listSize: ListSize
    customStyle: ListBuilder["style"]
  }
> = ({ listSize, customStyle, ...props }) => {
  const resolveLayout = () => {
    switch (listSize) {
      case ListSize.Small:
        return <SmallSection {...props} />
      case ListSize.Standard:
        return <StandardSection {...props} />
      case ListSize.Medium:
        return <MediumSection {...props} />
      case ListSize.Full:
        return <FullSection {...props} />
      default:
        return null
    }
  }
  if (
    customStyle?.background ||
    customStyle?.no_space_below ||
    customStyle?.paddingTopModifier
  ) {
    return (
      <BackgroundWrapper
        background={customStyle.background}
        no_space_below={customStyle.no_space_below}
        paddingTopModifier={customStyle.paddingTopModifier}
      >
        {resolveLayout()}
      </BackgroundWrapper>
    )
  }
  return resolveLayout()
}

export const TextWithHeading = styled(ListWrapper)<{
  customStyle: ListBuilder["style"]
}>`
  grid-template-areas: "Heading" "Article";

  ${({ theme, customStyle }) =>
    customStyle &&
    css`
      @media (min-width: ${theme.breakpoint.mobileHorizontal}px) {
        --text-max-width: ${customStyle.text_max_width}px;
      }
      li {
        img {
          border-radius: ${customStyle.rounded_images
            ? theme.size.borderRadiusSmall
            : 0};
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.mobileButtonFullWidth &&
    css`
      @media (max-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        a {
          width: 100% !important;
        }
      }
    `}

  ${({ customStyle }) =>
    customStyle?.text_as_image_description &&
    css`
      li {
        ${textWithDescriptionStyle}
      }
    `}

    ${({ customStyle }) =>
    customStyle?.margin_top &&
    css`
      ul {
        margin-top: ${customStyle.margin_top}px;
      }
    `}

  ${({ listLayout }) =>
    listLayout === ListLayout.OneColumnWithLeftText &&
    css`
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 140px;
    `}

  ${({ customStyle }) =>
    customStyle?.customerLovedKompakt &&
    css`
      background: red;
    `}
`

const HeadingText = styled(SectionHeading)`
  *:last-child {
    margin-bottom: 0 !important;
  }
  max-width: var(--text-max-width);

  ${reduceEmptySpaces};
`

const List: AppFunctionComponent<{
  layout: ListLayout
}> = ({ layout, ...props }) => {
  switch (layout) {
    case ListLayout.OneColumnNoMargin:
      return <OneColumnNoMarginList {...props} />
    case ListLayout.OneColumnWithLeftText:
      return <OneColumnWithLeftTextList {...props} />
    case ListLayout.OneColumn:
      return <OneColumnList {...props} />
    case ListLayout.TwoColumns:
      return <TwoColumnsList {...props} />
    case ListLayout.TwoColumnsLastWide:
      return <TwoColumnsLastWideList {...props} />
    case ListLayout.ThreeColumns:
      return <ThreeColumnsList {...props} />
    case ListLayout.FourColumns:
      return <FourColumnsList {...props} />
    case ListLayout.FiveColumns:
      return <FiveColumnsList {...props} />
    case ListLayout.SixColumns:
      return <SixColumnsList {...props} />
    case ListLayout.EcommerceSingleColumn:
      return <EcommerceSingleColumnList {...props} />
    case ListLayout.Accordion:
      return <BasicAccordionList {...props} />
    case ListLayout.Careers:
      return <CareerList {...props} />
    default:
      return null
  }
}

type Props = Omit<ListBuilder, "id" | "internal">

const ListSection: AppFunctionComponent<Props> = ({
  listLayout,
  anchorId,
  listSize,
  text,
  style,
  children,
}) => {
  return (
    <SectionBackgroundWrapper customStyle={style}>
      <TextWithHeading
        layout={SectionLayout.Text}
        listSize={listSize}
        customStyle={style}
      >
        <ScrollAnchor id={anchorId} />
        {text && (
          <HeadingText>
            <RichText content={text} />
          </HeadingText>
        )}
        <List layout={listLayout}>{children}</List>
      </TextWithHeading>
    </SectionBackgroundWrapper>
  )
}

export default ListSection
